// agentMessages.js — build the user message content for the agent loop.
//
// Text-only turns stay plain strings (unchanged behavior). When the user
// attaches images, we build an OpenAI-style multimodal content array
// ([{type:'text'}, {type:'image_url', image_url:{url}}]) so vision-capable
// models actually SEE the image instead of just a "Anexos: x.png" line.
// The Worker adapters forward this array as-is (OpenAI-compat) or translate
// it (Anthropic). Pure + testable.

// Strip the legacy "Anexos (N):" metadata block when we also send image_url
// blocks — otherwise the model sees duplicate hints and may claim it cannot see.
function stripAttachmentMetadata(text) {
  if (typeof text !== 'string') return text;
  return text.replace(/\n\nAnexos \(\d+\):\n(?:- .+\n?)+$/i, '').trim();
}

function imageAttachments(attachments) {
  if (!Array.isArray(attachments)) return [];
  return attachments.filter(
    (a) =>
      a &&
      (a.kind === 'image' || (typeof a.type === 'string' && a.type.startsWith('image/'))) &&
      typeof a.preview === 'string' &&
      a.preview.startsWith('data:'),
  );
}

/**
 * Build the `content` for a user turn.
 * @param {string} text
 * @param {Array} [attachments]
 * @returns {string | Array<object>} plain string when no images, else a
 *   multimodal content array.
 */
export function buildUserContent(text, attachments) {
  const images = imageAttachments(attachments);
  const baseText = images.length > 0 ? stripAttachmentMetadata(text) : text;
  if (images.length === 0) return baseText;
  const blocks = [];
  if (baseText && baseText.trim()) blocks.push({ type: 'text', text: baseText });
  for (const img of images) {
    blocks.push({ type: 'image_url', image_url: { url: img.preview } });
  }
  // Guard: if somehow no blocks (empty text + no images), fall back to text.
  return blocks.length > 0 ? blocks : baseText;
}

/** True when a message content is a multimodal array (has an image block). */
export function hasImageContent(content) {
  return Array.isArray(content) && content.some((b) => b && b.type === 'image_url');
}
